import type {Issue} from "../entities/issue.entity";

/** The three fields bucketing and ordering need — an `Issue` satisfies it; tests can pass plain stand-ins. */
type CalendarEntry = Pick<Issue, 'start' | 'end' | 'title'>;

/**
 * Buckets the calendar row's entries (public holidays, absences — the issues of the calendar
 * project) onto the given days for "Mein Kalender" (Anke, 2026-10-16: "die dinge, die in der
 * kalenderzeile stehen auch ganz schmal und dezent ... mit darstellen").
 *
 * An entry covers every day from its start up to, but NOT including, its end — the same
 * exclusive end the timeline's calendar row uses (`interval.end.minus({days: 1})` in
 * timeline-data-providers.factory.ts), so a one-day holiday (start 31.10., end 01.11.) shows
 * on the 31st only. An entry whose end is not after its start still shows on its start day.
 *
 * Only days in `isoDates` get a bucket, and only when something falls on them. Within a day
 * entries are ordered by start, then title.
 */
export function bucketCalendarEntriesByDay<T extends CalendarEntry>(entries: readonly T[], isoDates: readonly string[]): Map<string, T[]> {
    const byDate = new Map<string, T[]>();
    const sorted = [...entries].sort((a, b) => a.start.toMillis() - b.start.toMillis() || a.title.localeCompare(b.title));

    for (const isoDate of isoDates) {
        const onThisDay = sorted.filter(entry => {
            const start = entry.start.toISODate()!;
            const end = entry.end.toISODate()!;
            return isoDate === start || (start <= isoDate && isoDate < end);
        });
        if (onThisDay.length > 0) {
            byDate.set(isoDate, onThisDay);
        }
    }

    return byDate;
}
